import {
  YStack,
  View,
  AnimatePresence,
  Anchor,
  isWeb,
  Image,
  Text,
  Paragraph,
  XStack,
} from 'tamagui'
import HerosparkLogoSvg from '@assets/hero/herospark-logo.svg'
import HerosparkCircleLogoSvg from '@assets/hero/herospark-circle-logo.svg'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { SchoolSelect } from '@components/SchoolSelect'
import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetSchoolsByEmail } from '@services/api/graphql/student'
import { useToastController } from '@tamagui/toast'
import { useSignIn } from '@hooks/useUser'
import { router } from 'expo-router'
import { useSession } from '@hooks/useSession'
import { AUTH_TOKEN, NATIVE_BASE_URL } from '@constants/storages'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import {
  KeyboardAvoidingView,
  Platform,
  VirtualizedList,
  Keyboard,
} from 'react-native'
import { useThemeParams } from '@hooks/useThemeParams'
import { LoadingScreen } from '@components/LoadingScreen'
import { ISchool } from '@dtos/Theme'
import { faEnvelope, faKey } from '@fortawesome/pro-light-svg-icons'

type Step = 'email' | 'school' | 'password'

type School = {
  id: string
  name: string
  organizationId: number
  url: string
  logo?: string
}

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export default function Login() {
  const { t } = useTranslation('Login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>()
  const [currentStep, setCurrentStep] = useState<Step>('email')
  const [schools, setSchools] = useState<School[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [themeSchool, setThemeSchool] = useState<ISchool | null>(null)
  const [hasEmailError, setHasEmailError] = useState(false)
  const [hasPasswordError, setHasPasswordError] = useState(false)
  const [isKeyboardVisible, setKeyboardVisible] = useState(false)
  const toast = useToastController()
  const { signIn } = useSignIn()
  const { getSchoolsByEmail } = useGetSchoolsByEmail()
  const { signInByToken } = useSession()
  const { setItem: setStorageSchoolUrl } = useAsyncStorage(NATIVE_BASE_URL)
  const { setItem: setStorageToken } = useAsyncStorage(AUTH_TOKEN)
  const {
    data: themeParams,
    loading: themeParamsLoading,
    refreshParams,
  } = useThemeParams()

  const hasThemeSchool = useMemo(() => {
    return Boolean(themeSchool)
  }, [themeSchool])

  const displaySchoolSelect = useMemo(() => {
    return currentStep !== 'email' && !hasThemeSchool
  }, [currentStep, hasThemeSchool])

  const isButtonDisabled = useMemo(() => {
    if (hasThemeSchool) {
      return !password.trim() || !email.trim() || !isValidEmail(email)
    }

    switch (currentStep) {
      case 'email':
        return !email.trim() || !isValidEmail(email)
      case 'school':
        return !selectedSchool
      case 'password':
        return !password.trim()
    }
  }, [currentStep, email, selectedSchool, password, hasThemeSchool])

  const isCurrentStepPassword = useMemo(() => {
    return currentStep === 'password'
  }, [currentStep])

  const isCurrentStepSchool = useMemo(() => {
    return currentStep === 'school'
  }, [currentStep])

  const hasOnlyOneSchool = useMemo(() => {
    return schools.length === 1
  }, [schools])

  const animationProps = {
    opacity: 0,
    y: -10,
  }

  const themeSchoolContainerStyles = useMemo(
    () => ({
      maxWidth: hasThemeSchool ? 390 : 640,
      padding: hasThemeSchool ? ('$size.spacing-md' as const) : 0,
      borderRadius: hasThemeSchool ? ('$border-radius-md' as const) : 0,
      backgroundColor: hasThemeSchool ? ('$neutral-0' as const) : 'transparent',
      gap: hasThemeSchool
        ? ('$size.spacing-md' as const)
        : isKeyboardVisible && !isWeb
          ? 40
          : 141,
      $gtSm: {
        padding: hasThemeSchool ? ('$size.spacing-lg' as const) : 0,
      },
    }),
    [hasThemeSchool, isKeyboardVisible],
  )

  const themeSchoolContentStyles = useMemo(
    () => ({
      gap: hasThemeSchool
        ? ('$size.spacing-md' as const)
        : ('$size.spacing-xl' as const),
    }),
    [hasThemeSchool],
  )

  const themeSchoolAnchorStyles = useMemo(
    () => ({
      color: hasThemeSchool
        ? ('$primary-300' as const)
        : ('$neutral-800' as const),
      fontWeight: hasThemeSchool ? ('700' as const) : ('400' as const),
      fontFamily: hasThemeSchool
        ? ('$mulishBold' as const)
        : ('$mulishRegular' as const),
      fontSize: hasThemeSchool
        ? ('$font-size-xxxs' as const)
        : ('$font-size-xxs' as const),
      width: '100%',
    }),
    [hasThemeSchool],
  )

  const handleEmailChange = (text: string) => {
    setHasEmailError(false)

    if (currentStep !== 'email') {
      setCurrentStep('email')
      setSchools([])
      setSelectedSchool(undefined)
      setHasPasswordError(false)
      setPassword('')
    }

    setEmail(text)
  }

  const handlePasswordChange = (text: string) => {
    setPassword(text)
    setHasPasswordError(false)
  }

  const handleClearEmail = () => {
    setEmail('')
    setHasEmailError(false)
  }

  const fetchSchoolsByEmail = async (email: string, page = 1) => {
    if (!email.trim()) return null

    const isFirstPage = page === 1
    if (isFirstPage) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }

    try {
      const { data } = await getSchoolsByEmail({
        variables: {
          email,
          page,
          perPage: 10,
        },
      })

      const {
        schools: newSchools,
        totalPages: total,
        currentPage: pageNumber,
      } = data.studentSchools

      if (newSchools.length === 0 && isFirstPage) {
        return null
      }

      setTotalPages(total)
      setCurrentPage(pageNumber)

      return newSchools
    } catch (error) {
      toastErrorMessage(
        'Erro inesperado',
        'Ocorreu um erro ao buscar as escolas. Tente novamente.',
      )
      return null
    } finally {
      if (isFirstPage) {
        setIsLoading(false)
      } else {
        setIsLoadingMore(false)
      }
    }
  }

  const toastErrorMessage = (titulo: string, mensagem: string) => {
    toast.show(titulo, {
      message: mensagem,
      type: 'error',
    })
  }

  const handleLoadMore = async () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1
      const newSchools = await fetchSchoolsByEmail(email, nextPage)

      if (newSchools) {
        setSchools((prevSchools) => [...prevSchools, ...newSchools])
      }
    }
  }

  const handleContinue = async () => {
    if (hasThemeSchool) {
      handleLogin()
      return
    }

    switch (currentStep) {
      case 'email': {
        const schools = await fetchSchoolsByEmail(email)
        if (schools) {
          setSchools(schools)
          setCurrentStep('school')
          setHasEmailError(false)
        } else {
          setHasEmailError(true)
        }
        break
      }
      case 'school':
        setCurrentStep('password')
        break
      case 'password':
        handleLogin()
        break
    }
  }

  const handleSchoolChange = (school: School) => {
    setSelectedSchool(school)
    setCurrentStep('password')
    setPassword('')
  }

  const handleLogin = async () => {
    if (!selectedSchool) return

    try {
      const selectedSchoolId = hasThemeSchool
        ? themeSchool?.id
        : selectedSchool?.id
      const organizationId = hasThemeSchool
        ? themeSchool?.organizationId
        : selectedSchool?.organizationId

      if (!selectedSchoolId || !organizationId) {
        toastErrorMessage('Erro ao fazer login', 'Escola não encontrada.')
        return
      }

      const { data } = await signIn({
        variables: {
          auth: {
            email,
            password,
            schoolId: selectedSchoolId.toString(),
            organizationId,
          },
        },
      })

      const { status, token: tokenFromResponse } = data?.userSignIn ?? {
        status: 'error',
        error: 'Erro inesperado',
      }

      if (status === 'success' && tokenFromResponse) {
        await setStorageToken(tokenFromResponse)
        await setStorageSchoolUrl(selectedSchool?.url)

        const signInSuccess = await signInByToken(tokenFromResponse)

        await refreshParams()

        if (signInSuccess) {
          setHasPasswordError(false)
          isWeb ? (window.location.href = '/') : router.replace('/')
        } else {
          setHasPasswordError(true)
          toastErrorMessage(
            'Erro ao fazer login',
            'Não foi possível autenticar o usuário.',
          )
        }
      } else {
        setHasPasswordError(true)
      }
    } catch (error) {
      setHasPasswordError(true)
      toastErrorMessage(
        'Erro ao fazer login',
        'Ocorreu um erro ao fazer login. Tente novamente.',
      )
    }
  }

  useEffect(() => {
    if (hasOnlyOneSchool && isCurrentStepSchool) {
      setSelectedSchool(schools[0])
      setCurrentStep('password')
    }
  }, [schools, isCurrentStepSchool, hasOnlyOneSchool])

  useEffect(() => {
    if (!isWeb) return
    const { school } = themeParams ?? {}

    if (school) {
      setThemeSchool(school)
    }
  }, [themeParams, themeParamsLoading])

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        setKeyboardVisible(true)
      },
    )
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setKeyboardVisible(false)
      },
    )

    return () => {
      keyboardDidShowListener.remove()
      keyboardDidHideListener.remove()
    }
  }, [])

  if (themeParamsLoading) {
    return <LoadingScreen />
  }

  const getItem = (_data: unknown, index: number) => ({
    id: String(index),
    content: 'login',
  })

  const renderItem = () => (
    <View
      backgroundColor="$background-100"
      width="100%"
      minHeight="100%"
      paddingVertical={102}
      paddingHorizontal="$size.spacing-xs"
      alignItems="center"
      flex={1}
      gap="$size.spacing-xs"
    >
      <YStack width="100%" alignItems="center" {...themeSchoolContainerStyles}>
        {hasThemeSchool ? (
          <YStack width="100%" alignItems="center" gap="$size.spacing-xs">
            {themeSchool?.logo && (
              <Image
                source={{ uri: themeSchool?.logo }}
                alt={themeSchool?.name}
                width={220}
                height={71}
                resizeMode="contain"
              />
            )}
            <Paragraph
              color="$neutral-800"
              fontSize="$font-size-md"
              textAlign="center"
              lineHeight={30}
            >
              <Text color="$neutral-800" fontWeight={700}>
                {themeSchool?.name}
              </Text>
              {' está feliz em te receber!'}
            </Paragraph>

            <Text
              color="$neutral-600"
              fontSize="$font-size-xs"
              fontFamily="$mulishRegular"
              lineHeight={24}
            >
              Entre com o seu email e senha
            </Text>
          </YStack>
        ) : (
          <HerosparkLogoSvg height={43} width={220} />
        )}

        <YStack width="100%" {...themeSchoolContentStyles}>
          <YStack gap="$size.spacing-xs">
            <AnimatePresence>
              <YStack
                key="email"
                animation="quick"
                enterStyle={animationProps}
                exitStyle={animationProps}
              >
                <Input
                  icon={faEnvelope}
                  label={t('email.label')}
                  placeholder={t('email.placeholder')}
                  value={email}
                  onChangeText={handleEmailChange}
                  onClear={handleClearEmail}
                  autoComplete="email"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  errorMessage={hasEmailError ? t('email.error') : false}
                  displayErrorIcon={false}
                />
              </YStack>

              {displaySchoolSelect && (
                <YStack
                  key="school"
                  animation="quick"
                  enterStyle={animationProps}
                  exitStyle={animationProps}
                >
                  <SchoolSelect
                    label={t('school.label')}
                    description={t('school.description')}
                    options={schools}
                    value={selectedSchool}
                    onValueChange={handleSchoolChange}
                    autoComplete="current-password"
                    onLoadMore={handleLoadMore}
                    isLoadingMore={isLoadingMore}
                    hasMoreItems={currentPage < totalPages}
                    onOpenSchoolSelect={() => Keyboard.dismiss()}
                  />
                </YStack>
              )}

              {(isCurrentStepPassword || hasThemeSchool) && (
                <YStack
                  key="password"
                  animation="quick"
                  enterStyle={animationProps}
                  exitStyle={animationProps}
                >
                  <Input
                    icon={faKey}
                    label={t('password.label')}
                    placeholder={t('password.placeholder')}
                    value={password}
                    onChangeText={handlePasswordChange}
                    secureTextEntry
                    errorMessage={
                      hasPasswordError ? t('password.error') : false
                    }
                    displayErrorIcon={false}
                  />
                </YStack>
              )}
            </AnimatePresence>
          </YStack>

          <YStack gap="$size.spacing-xxxs">
            <Button
              variant="primary"
              size="large"
              onPress={handleContinue}
              disabled={isButtonDisabled || isLoading}
              animation="quick"
              pressStyle={{ scale: 0.97 }}
            >
              {isCurrentStepPassword || hasThemeSchool
                ? t('button.login')
                : t('button.continue')}
            </Button>

            {(isCurrentStepPassword || hasThemeSchool) && (
              <Anchor
                {...themeSchoolAnchorStyles}
                cursor="pointer"
                href={`${selectedSchool?.url}/users/password/new`}
                target="_blank"
              >
                {t('password.forgot-password')}
              </Anchor>
            )}
          </YStack>
        </YStack>
      </YStack>

      {hasThemeSchool && (
        <XStack
          alignItems="center"
          justifyContent="flex-end"
          gap="$size.spacing-xxs"
          width="100%"
          maxWidth={390}
        >
          <HerosparkCircleLogoSvg height={26} width={26} />
          <Text
            color="$neutral-400"
            fontSize="$font-size-xs"
            fontFamily="$mulishRegular"
            lineHeight={24}
          >
            Feito por Herospark
          </Text>
        </XStack>
      )}
    </View>
  )

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flexGrow: 1 }}
    >
      <View bg="$background-100" flex={1}>
        <VirtualizedList
          data={[{}]}
          renderItem={renderItem}
          keyExtractor={() => 'login'}
          getItem={getItem}
          getItemCount={() => 1}
          keyboardShouldPersistTaps="handled"
        />
      </View>
    </KeyboardAvoidingView>
  )
}
